/**
 * DunningBanner — renders at the top of every authenticated page when the
 * workspace's billing is in a degraded state (past_due or canceled).
 *
 * State sources:
 *   - workspace.billing_status from useAuth() (already-loaded, no network)
 *   - profile.is_owner from useAuth() (gates the CTA but not the banner
 *     itself — non-owners still see the message so they know to talk to
 *     their admin)
 *
 * Returns null for healthy states ('active', 'trialing', 'incomplete',
 * 'none'). Zero render cost when nothing's wrong.
 *
 * For 'past_due':
 *   - Owners see "Your last payment failed" + Update payment method CTA
 *   - Non-owners see the same headline but no CTA — message tells them
 *     to contact the workspace owner
 *
 * For 'canceled':
 *   - Owners see "Your subscription was canceled" + Reactivate CTA
 *     (which opens the Stripe portal — Stripe handles reactivation)
 *   - Non-owners see "Your subscription was canceled" + contact-owner copy
 *
 * The CTA opens the Stripe billing portal via the same create-portal-session
 * Edge Function used by Settings/Billing's "Manage subscription" button.
 */

import { useState } from 'react'
import { AlertTriangle, ExternalLink, Loader2 } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

type DunningCopy = {
  headline: string
  subline: string
  ctaLabel: string
}

const COPY: Record<'past_due' | 'canceled', DunningCopy> = {
  past_due: {
    headline: 'Your last payment failed',
    subline: 'Update your payment method to keep using Orkestra without interruption.',
    ctaLabel: 'Update payment method',
  },
  canceled: {
    headline: 'Your subscription was canceled',
    subline: 'Reactivate to restore access to your workspace.',
    ctaLabel: 'Reactivate subscription',
  },
}

export default function DunningBanner() {
  const { workspace, profile } = useAuth()
  const [opening, setOpening] = useState(false)

  // Bail-out fast for healthy states. The vast majority of renders go through
  // this branch.
  const status = workspace?.billing_status
  if (status !== 'past_due' && status !== 'canceled') {
    return null
  }

  // Internal workspaces don't have billing — never show the banner even if
  // billing_status somehow got a degraded value.
  if (workspace?.internal) {
    return null
  }

  const isOwner = !!profile?.is_owner
  const copy = COPY[status]

  const handleOpenPortal = async () => {
    setOpening(true)
    try {
      const { data, error } = await supabase.functions.invoke<{ url: string }>(
        'create-portal-session',
        { body: { return_url: `${window.location.origin}/app/settings/billing` } },
      )

      if (error) {
        // Same body-extraction pattern as Settings.tsx handleUpgrade —
        // build a message string, never throw inside the try.
        const ctx = (error as any).context
        let message = error.message
        if (ctx && typeof ctx.text === 'function') {
          try {
            const text = await ctx.text()
            const parsed = JSON.parse(text)
            message = parsed?.error || parsed?.message || message
          } catch { /* keep default */ }
        }
        throw new Error(message)
      }
      if (!data?.url) throw new Error('No portal URL returned')

      window.location.href = data.url
    } catch (err) {
      console.error('Dunning banner portal open error:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to open billing portal')
      setOpening(false)
    }
  }

  // past_due is amber — actionable, not yet broken.
  // canceled is red — currently broken, urgent.
  // Tones now reference themed tokens; CTA hover bg uses the *-border token
  // (~0.20 alpha) as the closest analogue to the original 0.18-alpha replace().
  const tone = status === 'past_due'
    ? { bg: 'var(--warning-dim)', border: 'var(--warning-border)', icon: 'var(--warning)', accent: 'var(--warning)', ctaBg: 'var(--warning-border)' }
    : { bg: 'var(--danger-dim)',  border: 'var(--danger-border)',  icon: 'var(--danger)',  accent: 'var(--danger)',  ctaBg: 'var(--danger-border)' }

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 20px',
        background: tone.bg,
        borderBottom: `1px solid ${tone.border}`,
        flexShrink: 0,
      }}
    >
      <AlertTriangle style={{ width: 16, height: 16, color: tone.icon, flexShrink: 0 }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>
          {copy.headline}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-secondary)' }}>
          {isOwner
            ? copy.subline
            : 'Contact your workspace owner to restore billing.'}
        </p>
      </div>

      {isOwner && (
        <button
          onClick={handleOpenPortal}
          disabled={opening}
          style={{
            padding: '7px 12px',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            background: opening ? 'var(--hover-bg)' : tone.ctaBg,
            border: `1px solid ${tone.border}`,
            color: opening ? 'var(--ink-tertiary)' : tone.accent,
            cursor: opening ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            flexShrink: 0,
            transition: 'all 150ms ease',
          }}
        >
          {opening
            ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} />
            : (
              <>
                {copy.ctaLabel}
                <ExternalLink style={{ width: 11, height: 11 }} />
              </>
            )}
        </button>
      )}
    </div>
  )
}
